// FadeImg.jsx
// <img> that fades in when its bitmap is actually ready to paint, instead of
// popping in whenever fetch + decode happens to finish. Overlay cards do a
// container-level fade-through (fade out → swap content → fade in), but a
// freshly mounted <img> only paints once the browser has decoded it — if
// that lands after the container fade, the image hard-pops at full opacity.
// Wrapping it here gives slow images their own soft fade the moment they're
// ready, while already-decoded images stay in sync with the container fade.
// Opacity-only transition — composites for free (OBS perf rule).

import { useEffect, useRef, useState } from "react";

export default function FadeImg({ src, durationMs = 400, style, ...rest }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  // Runs on every src change (and first mount). If the image is already
  // complete — decoded + cached, common after the rotation's first lap —
  // mark it ready immediately so it fades with its container instead of
  // waiting for an onLoad that may have fired before React attached it.
  useEffect(() => {
    const el = ref.current;
    setReady(!!(el && el.complete && el.naturalWidth > 0));
  }, [src]);

  return (
    <img
      ref={ref}
      src={src}
      onLoad={() => setReady(true)}
      style={{
        ...style,
        opacity: ready ? 1 : 0,
        transition: `opacity ${durationMs}ms ease`,
      }}
      {...rest}
    />
  );
}
